import React, { useMemo } from 'react'
import DashboardSectionCard from '@components/pages/Dashboard/DashboardSectionCard.component'
import DashboardMetricItem from '@components/pages/Dashboard/DashboardMetricItem.component'
import DashboardHalfDonutChart from '@components/pages/Dashboard/MetricCharts/DashboardHalfDonutChart.component'
import type { RangeKey } from '@dataTypes/dashboard.types'
import type { ChairTimeOverviewResult } from '@services/api/dashboard/chair-time.api'
import { buildChairTimeOverviewFromMock } from '@utils/chairTimeMock.util'
import { isDashboardDevMocksEnabled } from '@utils/dashboardDevMocks.util'
import {
  hasNonZeroDashboardValues,
  isDashboardSectionPending,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import { metricDataToItemPropsWithLayoutClass } from '@utils/dashboardMetrics.util'

type ChairTimeCardProps = {
  range: RangeKey
  chairTimeOverview: ChairTimeOverviewResult | null
  loading: boolean
}

export function ChairTimeCard({
  range,
  chairTimeOverview,
  loading,
}: ChairTimeCardProps): React.ReactElement {
  const useMocks = isDashboardDevMocksEnabled()
  const mockChairTimeOverview = useMemo(
    () => buildChairTimeOverviewFromMock(range),
    [range],
  )
  const chairTime = useMemo(() => {
    if (useMocks) return mockChairTimeOverview.chairTime
    const efficiency = chairTimeOverview?.efficiencyRate ?? 0
    const goal = chairTimeOverview?.efficiencyGoal ?? 0
    const scheduledSlice = chairTimeOverview?.scheduledSlicePct ?? 0
    const availableSlice = chairTimeOverview?.availableSlicePct ?? 0
    return {
      primaryValue: efficiency,
      secondaryValue: goal,
      valueFormatter: (val: number): string => val + '%',
      slices: [
        { name: 'Scheduled', value: scheduledSlice, color: 'var(--blue-400)' },
        {
          name: 'Available',
          value: availableSlice,
          color: 'var(--orange-300)',
        },
      ],
      totalHrs: chairTimeOverview?.totalChairTime ?? '0 hrs',
      schedHrs: chairTimeOverview?.scheduledTime ?? '0 hrs',
      openHrs: chairTimeOverview?.openChairTime ?? '0 hrs',
    }
  }, [useMocks, mockChairTimeOverview.chairTime, chairTimeOverview])
  const metrics = useMemo(() => {
    if (useMocks) return mockChairTimeOverview.metrics
    return [
      {
        id: 'chair-total',
        leftLabel: 'Total chair time',
        value: chairTime.totalHrs,
      },
      {
        id: 'chair-scheduled',
        leftLabel: 'Scheduled time',
        value: chairTime.schedHrs,
      },
      {
        id: 'chair-open',
        leftLabel: 'Open chair time',
        value: chairTime.openHrs,
      },
    ]
  }, [useMocks, mockChairTimeOverview.metrics, chairTime])
  // Total/open minutes are capacity, not activity: only scheduled time and the
  // efficiency rate establish data. Use the raw response minutes when the
  // response is what is rendered; mock data renders its own scheduled value.
  const hasData = hasNonZeroDashboardValues(
    useMocks ? chairTime.schedHrs : chairTimeOverview?.scheduledMinutes,
    chairTime.primaryValue,
  )
  const isEmpty = shouldShowDashboardSectionEmpty(loading, hasData)
  const pending = isDashboardSectionPending(loading, chairTimeOverview != null)

  return (
    <DashboardSectionCard
      title="Chair time"
      titleHref="/dashboard/chair-time"
      className="dashboard-acceptance-card dashboard-chair-time-card"
      loading={pending}
      skeleton={{ metrics: 3, block: 'chart' }}
      isEmpty={isEmpty}
      disableTitleLink={isEmpty}
    >
      <div className="dashboard-overview-acceptance-content">
        <div className="dashboard-overview-acceptance-metrics-row">
          {metrics.map((metric) => (
            <DashboardMetricItem
              key={metric.id ?? metric.leftLabel}
              {...metricDataToItemPropsWithLayoutClass(
                metric,
                'dashboard-overview-acceptance-metric',
              )}
            />
          ))}
        </div>
        <DashboardMetricItem
          className="dashboard-overview-acceptance-metric dashboard-overview-acceptance-metric--full"
          leftLabel="Efficiency rate"
          value={chairTime.valueFormatter(chairTime.primaryValue)}
          valueOver={chairTime.valueFormatter(chairTime.secondaryValue)}
        />
        <div className="dashboard-overview-acceptance-chart-box">
          <DashboardHalfDonutChart
            title="Chair time"
            primaryValue={chairTime.primaryValue}
            secondaryValue={chairTime.secondaryValue}
            valueFormatter={chairTime.valueFormatter}
            slices={chairTime.slices}
            hideCenterMetric
            className="dashboard-overview-acceptance-chart"
          />
          <div className="dashboard-overview-acceptance-legend">
            <span className="dashboard-overview-acceptance-legend-item">
              <span
                className="dashboard-overview-acceptance-legend-dot"
                style={{ background: 'var(--blue-400)' }}
              />
              Scheduled
            </span>
            <span className="dashboard-overview-acceptance-legend-item">
              <span
                className="dashboard-overview-acceptance-legend-dot"
                style={{ background: 'var(--orange-300)' }}
              />
              Available
            </span>
          </div>
        </div>
      </div>
    </DashboardSectionCard>
  )
}
